'use client';

import { CommonLocals } from '@pioz/mlm-common';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FieldValues, UseFormTrigger } from 'react-hook-form';
import { Dialog } from '../Dialog';
import { TranslationText } from '../translations';
import { getTranslation } from '../translations/get-translation';
import { SubmitButton, SubmitButtonProps } from './SubmitButton';

interface Props<T extends FieldValues> extends SubmitButtonProps {
  isValid: boolean;
  isDirty?: boolean;
  trigger: UseFormTrigger<T>;
  locale: CommonLocals;
  showErrorDialog?: boolean;
}

export function SubmitButtonWithValidation<T extends FieldValues>({
  isValid,
  isDirty = true,
  trigger,
  locale,
  showErrorDialog,
  ...restProps
}: Props<T>) {
  const [showErrors, setShowErrors] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);

  const id = Math.random().toString(36).substring(2, 15);

  const { data: submit_button_validation_error } = useQuery({
    queryKey: ['submit_button_validation_error', { language: locale }],
    queryFn: () => getTranslation({ language: locale, basename: 'submit_button_validation_error' }),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFormReady(true);
    }, 500); // Adjust this delay as needed

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!showErrorDialog) return;

    setShowErrors(true);
  }, [showErrorDialog]);

  const disabled = restProps.disabled === true ? restProps.disabled : !isDirty;

  return (
    <>
      {!isValid && isFormReady ? (
        <button
          type="button"
          onClick={async () => {
            if (await trigger()) {
              document.getElementById(id)?.click();
            } else {
              setShowErrors(true);
            }
          }}
          className={restProps.className}
          style={restProps.style}
          disabled={disabled}
          suppressHydrationWarning
        >
          {restProps.children}
        </button>
      ) : (
        <SubmitButton id={id} {...restProps} disabled={disabled} suppressHydrationWarning>
          {restProps.children}
        </SubmitButton>
      )}
      {showErrors && (
        <Dialog
          removeOpenButton
          contentClass="max-w-lg"
          onClose={() => {
            setShowErrors(false);
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          }}
        >
          <TranslationText text={submit_button_validation_error} />
        </Dialog>
      )}
    </>
  );
}
